
import { supabase } from '@/integrations/supabase/client';
import { DateTimeSlot, CancellationPolicy, ReschedulePolicy } from '@/types/dashboard';

/**
 * Serviço para gerenciar operações de consultas (cancelamento e reagendamento)
 */
export class AppointmentManagementService {
  /**
   * Cancela uma consulta
   * @param appointmentId ID da consulta
   * @param reason Motivo do cancelamento
   * @param cancelledBy Quem está cancelando (psychologist ou patient)
   */
  static async cancelAppointment(
    appointmentId: string,
    reason: string,
    cancelledBy: 'psychologist' | 'patient'
  ): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .update({
          status: 'cancelled',
          cancellation_reason: reason,
          cancelled_by: cancelledBy,
          cancelled_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', appointmentId)
        .select()
        .single();

      if (error) {
        console.error('Error cancelling appointment:', error);
        throw error;
      }

      // Registrar no histórico
      await this.addToHistory(appointmentId, 'cancelled', cancelledBy, reason);

      return data;
    } catch (error) {
      console.error('Error in cancelAppointment:', error);
      throw error;
    }
  }

  /**
   * Cria uma solicitação de reagendamento
   * @param appointmentId ID da consulta
   * @param reason Motivo do reagendamento
   * @param newDateTime Nova data e hora proposta
   * @param requestedBy Quem está solicitando (psychologist ou patient)
   */
  static async createRescheduleRequest(
    appointmentId: string,
    reason: string,
    newDateTime: Date,
    requestedBy: 'psychologist' | 'patient'
  ): Promise<any> {
    try {
      // Buscar dados da consulta original
      const { data: appointment, error: appointmentError } = await supabase
        .from('appointments')
        .select('date, time')
        .eq('id', appointmentId)
        .single();

      if (appointmentError) {
        throw appointmentError;
      }

      // Criar timestamp da consulta original
      const originalDateTime = new Date(`${appointment.date}T${appointment.time}`);

      // Criar solicitação de reagendamento
      const { data, error } = await supabase
        .from('reschedule_requests')
        .insert({
          appointment_id: appointmentId,
          requested_by: requestedBy,
          reason: reason,
          original_date_time: originalDateTime.toISOString(),
          proposed_date_time: newDateTime.toISOString(),
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating reschedule request:', error);
        throw error;
      }

      // Atualizar status da consulta
      await supabase
        .from('appointments')
        .update({
          status: 'pending_reschedule',
          updated_at: new Date().toISOString()
        })
        .eq('id', appointmentId);

      // Registrar no histórico
      await this.addToHistory(appointmentId, 'reschedule_requested', requestedBy, reason);

      return data;
    } catch (error) {
      console.error('Error in createRescheduleRequest:', error);
      throw error;
    }
  }

  /**
   * Confirma uma solicitação de reagendamento
   * @param rescheduleRequestId ID da solicitação de reagendamento
   */
  static async confirmRescheduleRequest(rescheduleRequestId: string): Promise<any> {
    try {
      // Buscar dados da solicitação
      const { data: request, error: requestError } = await supabase
        .from('reschedule_requests')
        .select('*')
        .eq('id', rescheduleRequestId)
        .single();

      if (requestError) {
        throw requestError;
      }

      // Atualizar solicitação como confirmada
      await supabase
        .from('reschedule_requests')
        .update({
          status: 'confirmed',
          responded_at: new Date().toISOString()
        })
        .eq('id', rescheduleRequestId);

      // Atualizar consulta com nova data e hora
      const newDate = new Date(request.proposed_date_time);
      await supabase
        .from('appointments')
        .update({
          date: newDate.toISOString().split('T')[0],
          time: newDate.toTimeString().split(' ')[0],
          status: 'scheduled',
          updated_at: new Date().toISOString()
        })
        .eq('id', request.appointment_id);

      // Registrar no histórico
      await this.addToHistory(request.appointment_id, 'rescheduled', 'system', 'Reagendamento confirmado');

      return request;
    } catch (error) {
      console.error('Error in confirmRescheduleRequest:', error);
      throw error;
    }
  }

  /**
   * Rejeita uma solicitação de reagendamento
   * @param rescheduleRequestId ID da solicitação de reagendamento
   * @param reason Motivo da rejeição
   */
  static async rejectRescheduleRequest(rescheduleRequestId: string, reason?: string): Promise<any> {
    try {
      // Buscar dados da solicitação
      const { data: request, error: requestError } = await supabase
        .from('reschedule_requests')
        .select('*')
        .eq('id', rescheduleRequestId)
        .single();

      if (requestError) {
        throw requestError;
      }

      // Atualizar solicitação como rejeitada
      await supabase
        .from('reschedule_requests')
        .update({
          status: 'rejected',
          response_reason: reason,
          responded_at: new Date().toISOString()
        })
        .eq('id', rescheduleRequestId);

      // Retornar status da consulta para agendada
      await supabase
        .from('appointments')
        .update({
          status: 'scheduled',
          updated_at: new Date().toISOString()
        })
        .eq('id', request.appointment_id);

      // Registrar no histórico
      await this.addToHistory(request.appointment_id, 'reschedule_rejected', 'system', reason);

      return request;
    } catch (error) {
      console.error('Error in rejectRescheduleRequest:', error);
      throw error;
    }
  }

  /**
   * Busca política de cancelamento para uma consulta
   * @param appointmentId ID da consulta
   */
  static async getCancellationPolicy(appointmentId: string): Promise<CancellationPolicy> {
    try {
      // Buscar psicólogo da consulta
      const { data: appointment, error: appointmentError } = await supabase
        .from('appointments')
        .select('psychologist_id')
        .eq('id', appointmentId)
        .single();

      if (appointmentError) {
        throw appointmentError;
      }

      // Buscar política do psicólogo
      const { data: policy, error } = await supabase
        .from('cancellation_policies')
        .select('*')
        .eq('psychologist_id', appointment.psychologist_id)
        .single();

      if (error) {
        console.error('Error fetching cancellation policy:', error);
        // Retornar política padrão se não encontrar
        return {
          id: '',
          psychologistId: appointment.psychologist_id,
          noFeeThreshold: 24,
          partialRefundThreshold: 12,
          partialRefundPercentage: 50,
          exceptions: {
            medical_emergency: true,
            technical_issues: true,
            weather_conditions: true
          }
        };
      }

      return {
        id: policy.id,
        psychologistId: policy.psychologist_id,
        noFeeThreshold: policy.no_fee_threshold,
        partialRefundThreshold: policy.partial_refund_threshold,
        partialRefundPercentage: policy.partial_refund_percentage,
        exceptions: policy.exceptions as { [key: string]: boolean; medical_emergency: boolean; technical_issues: boolean; weather_conditions: boolean; }
      };
    } catch (error) {
      console.error('Error in getCancellationPolicy:', error);
      throw error;
    }
  }

  /**
   * Busca política de reagendamento para uma consulta
   * @param appointmentId ID da consulta
   */
  static async getReschedulePolicy(appointmentId: string): Promise<ReschedulePolicy> {
    try {
      // Buscar psicólogo da consulta
      const { data: appointment, error: appointmentError } = await supabase
        .from('appointments')
        .select('psychologist_id')
        .eq('id', appointmentId)
        .single();

      if (appointmentError) {
        throw appointmentError;
      }

      // Buscar política do psicólogo
      const { data: policy, error } = await supabase
        .from('reschedule_policies')
        .select('*')
        .eq('psychologist_id', appointment.psychologist_id)
        .single();

      if (error) {
        console.error('Error fetching reschedule policy:', error);
        // Retornar política padrão se não encontrar
        return {
          id: '',
          psychologistId: appointment.psychologist_id,
          maxReschedulesPerAppointment: 2,
          minHoursBeforeAppointment: 24,
          exceptions: {
            medical_emergency: true,
            technical_issues: true,
            weather_conditions: true
          }
        };
      }

      return {
        id: policy.id,
        psychologistId: policy.psychologist_id,
        maxReschedulesPerAppointment: policy.max_reschedules_per_appointment,
        minHoursBeforeAppointment: policy.min_hours_before_appointment,
        exceptions: policy.exceptions as { [key: string]: boolean; medical_emergency: boolean; technical_issues: boolean; weather_conditions: boolean; }
      };
    } catch (error) {
      console.error('Error in getReschedulePolicy:', error);
      throw error;
    }
  }

  /**
   * Busca horários disponíveis para reagendamento
   * @param psychologistId ID do psicólogo
   * @param startDate Data de início
   * @param endDate Data de fim
   */
  static async getAvailableSlots(
    psychologistId: string,
    startDate: Date,
    endDate: Date
  ): Promise<DateTimeSlot[]> {
    try {
      const { data, error } = await supabase.rpc('get_available_slots', {
        p_psychologist_id: psychologistId,
        p_start_date: startDate.toISOString().split('T')[0],
        p_end_date: endDate.toISOString().split('T')[0]
      });

      if (error) {
        console.error('Error fetching available slots:', error);
        throw error;
      }

      return data?.map((slot: any) => ({
        id: slot.id,
        startTime: slot.start_time,
        endTime: slot.end_time
      })) || [];
    } catch (error) {
      console.error('Error in getAvailableSlots:', error);
      throw error;
    }
  }

  /**
   * Adiciona entrada ao histórico de consultas
   * @param appointmentId ID da consulta
   * @param action Ação realizada
   * @param performedBy Quem realizou
   * @param reason Motivo/detalhes
   */
  private static async addToHistory(
    appointmentId: string,
    action: string,
    performedBy: string,
    reason?: string
  ): Promise<void> {
    try {
      await supabase
        .from('appointment_history')
        .insert({
          appointment_id: appointmentId,
          action_type: action,
          reason: reason
        });
    } catch (error) {
      console.error('Error adding to history:', error);
      // Não propagar erro do histórico para não afetar operação principal
    }
  }
}

export default AppointmentManagementService;
